import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { supabase } from "../api/supabase";
import { Sparkles } from "lucide-react";
import { useState } from "react";

export function AuthPage() {
  const frontendUrl = import.meta.env.VITE_FRONTEND_URL;
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const handleSendOtp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email,
        options: {
          shouldCreateUser: true,
        },
      });

      if (error) throw error;

      setIsOtpSent(true);
      setMessage("Check your email for the OTP code!");
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "An unexpected error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email: email,
        token: otp,
        type: "email",
      });

      if (error) {
        console.error("OTP verification error:", error);
        throw error;
      }

      console.log("OTP verification success:", data);
      setMessage("Successfully signed in! Checking session...");

      // Check if we actually have a session
      const { data: sessionData } = await supabase.auth.getSession();
      console.log("Current session:", sessionData.session);

      if (sessionData.session) {
        setMessage("OTP verified! Redirecting...");
        setTimeout(() => {
          window.location.href = "/dashboard";
        }, 1000);
      } else {
        setMessage("Error: No session found after verification");
      }
    } catch (error) {
      console.error("Verification failed:", error);
      setMessage(
        `Error: ${
          error instanceof Error
            ? error.message
            : "An unexpected error occurred"
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>
        {`
          .dot-pattern {
            background-image: radial-gradient(#000 1px, transparent 1px);
            background-size: 16px 16px;
          }
        `}
      </style>

      <div className="relative flex flex-col items-center justify-center overflow-hidden min-h-screen">
        {/* Background gradient - matching landing page */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-blue-50 z-0"></div>

        {/* Dot pattern overlay */}
        <div className="absolute inset-0 z-10 pointer-events-none opacity-20 dot-pattern"></div>

        {/* Auth Container */}
        <div className="relative z-20 w-full max-w-md mx-auto px-4">
          <div className="bg-white rounded-lg border border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] p-6">
            {/* Header Section */}
            <div className="p-6">
              <div className="flex items-center gap-2 justify-center mb-2">
                <h1 className="text-2xl font-bold text-blue-600 tracking-wide">
                  AI News
                </h1>
                <span
                  className="inline-flex items-center px-3 py-1 text-xs font-bold uppercase tracking-wide 
                               bg-blue-300 text-black border border-black rounded-full 
                               shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]
                               hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] 
                               hover:translate-x-[1px] hover:translate-y-[1px]
                               transition-all"
                >
                  <Sparkles className="w-3 h-3 mr-1" aria-hidden="true" />
                  Beta
                </span>
              </div>

              <p className="text-center text-gray-600 text-sm font-medium">
                Sign in to get started
              </p>
            </div>

            {/* Auth Section */}
            <div className="pt-4">
              {/* Google Login */}
              <div className="mb-4">
                <Auth
                  supabaseClient={supabase}
                  appearance={{
                    theme: ThemeSupa,
                    variables: {
                      default: {
                        colors: {
                          brand: "rgb(59, 130, 246)",
                          brandAccent: "rgb(37, 99, 235)",
                          brandButtonText: "white",
                          defaultButtonBackground: "white",
                          defaultButtonBackgroundHover: "#f8f8f8",
                          defaultButtonBorder: "rgb(59, 130, 246)",
                          inputBackground: "white",
                          inputBorder: "rgb(59, 130, 246)",
                          inputBorderHover: "rgb(37, 99, 235)",
                          inputBorderFocus: "rgb(37, 99, 235)",
                        },
                        space: {
                          buttonPadding: "12px 20px",
                          inputPadding: "12px 16px",
                        },
                        borderWidths: {
                          buttonBorderWidth: "1px",
                          inputBorderWidth: "1px",
                        },
                        fonts: {
                          bodyFontFamily: `ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif`,
                          buttonFontFamily: `ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif`,
                        },
                      },
                    },
                  }}
                  providers={["google"]}
                  redirectTo={`${frontendUrl}/auth/callback`}
                  onlyThirdPartyProviders={true}
                  showLinks={false}
                />
              </div>

              {/* Divider */}
              <div className="flex items-center my-4">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="px-3 text-sm text-gray-500">or</span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>

              {/* OTP Form */}
              {!isOtpSent ? (
                <form onSubmit={handleSendOtp} className="space-y-4">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-3xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-3xl border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold"
                  >
                    {loading ? "Sending..." : "Send OTP Code"}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <div>
                    <label
                      htmlFor="otp"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Enter OTP Code
                    </label>
                    <input
                      type="text"
                      id="otp"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="Enter the 6-digit code from your email"
                      required
                      maxLength={6}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-3xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-3xl border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold"
                  >
                    {loading ? "Verifying..." : "Verify OTP"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsOtpSent(false)}
                    className="w-full bg-gray-200 text-gray-800 py-3 px-4 rounded-3xl border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 font-semibold"
                  >
                    Back to Email
                  </button>
                </form>
              )}

              {/* Message Display */}
              {message && (
                <div
                  className={`mt-4 p-3 rounded-md text-sm ${
                    message.includes("Success") ||
                    message.includes("Check your email") ||
                    message.includes("OTP code") ||
                    message.includes("OTP verified")
                      ? "bg-green-100 text-green-800 border border-green-200"
                      : "bg-red-100 text-red-800 border border-red-200"
                  }`}
                >
                  {message}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
